import React from "react";
import _ from "lodash";
import { useIntl } from "react-intl";
import { ColumnDefinition, RowDefinition } from "griddle-react";

import { CellDownloadFiles } from "../CellDownloadFiles";
import {
    CellDateRange,
    CellDateTime,
    CellTranslatableMap,
    CellTranslatableMapBadge,
    DateRange,
} from "@ui/Table/Cells";
import { Table } from "@ui/Table";
import { compose } from "@util/compose";
import { InjectedLocaleProps, withLocale } from "@localisation/components/withLocale";
import { Report, ReportStatus } from "../../models";
import { messages } from "../../messages";

interface CustomReportsTableProps {
    reports: Report[];
}

interface Row {
    entity: Report;
    period: DateRange;
    status: ReportStatus;
}

// Type the table component
type TypedTable = new () => Table<Row>;
const TypedTable = Table as TypedTable;

type Props = CustomReportsTableProps & InjectedLocaleProps;

const CustomReportsTable = ({ reports, locale }: Props) => {
    const { formatMessage } = useIntl();

    const data = reports.map((report) => ({
        entity: report,
        period: {
            start: report.startDate,
            end: report.endDate,
        },
        status: report.availableAt > new Date()
            ? new ReportStatus({
                name: "processing",
                label: {
                    en: "Processing",
                    fr: "En cours",
                },
            })
            : (typeof report.status === "string" ? null : report.status),
    }));

    return (
        <TypedTable
            data={data}
            pageSize={data.length}
            layout="basic"
        >
            <RowDefinition>
                <ColumnDefinition
                    key="period"
                    id="period"
                    title={formatMessage(messages.reportColumnPeriod)}
                    // sortMethod={(data: Row[], _column: string, sortAscending: boolean) => {
                    //     return data.sort((a, b) => {
                    //         if (sortAscending) {
                    //             return a.period.start.getTime() - b.period.start.getTime()
                    //                 || a.period.end.getTime() - b.period.end.getTime();
                    //         }
                    //         return b.period.start.getTime() - a.period.start.getTime()
                    //             || b.period.end.getTime() - a.period.end.getTime();
                    //     });
                    // }}
                    customComponent={CellDateRange}
                    sortable={false}
                />
                <ColumnDefinition
                    key="description"
                    id="entity.description"
                    title={formatMessage(messages.reportColumnDescription)}
                    sortable={false}
                />
                <ColumnDefinition
                    key="type"
                    id="entity.type.label"
                    title={formatMessage(messages.reportColumnType)}
                    customComponent={CellTranslatableMap(locale)}
                    sortable={false}
                />
                <ColumnDefinition
                    key="status"
                    id="status"
                    title={formatMessage(messages.reportColumnStatus)}
                    customComponent={CellTranslatableMapBadge(locale, {
                        mapKey: "label",
                        idKey: "name",
                        color: {
                            "not-processed": "light",
                            "processing": "warning",
                            "processed": "success",
                            "error": "danger",
                            "processing-paused": "light",
                        },
                    })}
                    sortable={false}
                />
                <ColumnDefinition
                    key="generatedBy"
                    id="entity.generatedBy.name"
                    title={formatMessage(messages.reportColumnGeneratedBy)}
                    sortable={false}
                />
                <ColumnDefinition
                    key="createdAt"
                    id="entity.createdAt"
                    title={formatMessage(messages.reportColumnCreatedAt)}
                    customComponent={CellDateTime}
                    sortable={false}
                />
                <ColumnDefinition
                    key="files"
                    id="entity.files"
                    sortable={false}
                    title={formatMessage(messages.download)}
                    customComponent={CellDownloadFiles}
                />
            </RowDefinition>
        </TypedTable>
    );
};

const ConnectedCustomReportsTable = compose<Props, CustomReportsTableProps>(
    withLocale,
)(CustomReportsTable);

export {
    CustomReportsTable as CustomReportsTablePresenter,
    ConnectedCustomReportsTable as CustomReportsTable,
    Props as CustomReportsTableProps,
    Row as CustomReportsTableRow,
};
